import { Box, Stack, Tabs, Text } from '@mantine/core';
import { type BodyDiagramData } from '@/pages/PatientTimelinePage/services/bodyDiagram';

interface BodyDiagramViewProps {
  data: BodyDiagramData;
  searchTerm?: string;
}

export function BodyDiagramView({ data }: BodyDiagramViewProps) {
  const { view, notes, answerData } = data;
  const hasFront = view.front && answerData.frontImage;
  const hasBack = view.back && answerData.backImage;

  if (!hasFront && !hasBack) {
    return null;
  }

  // If both views exist, show tabs; otherwise show single image
  if (hasFront && hasBack) {
    return (
      <Stack gap="xs">
        {notes.label && (
          <Text size="sm" fw={500}>
            {notes.label}
          </Text>
        )}
        <Tabs defaultValue="front">
          <Tabs.List>
            <Tabs.Tab value="front">Front</Tabs.Tab>
            <Tabs.Tab value="back">Back</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="front" pt="xs">
            <Box
              style={{
                maxWidth: '100%',
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <img
                src={answerData.frontImage}
                alt="Body diagram - Front view"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
          </Tabs.Panel>
          <Tabs.Panel value="back" pt="xs">
            <Box
              style={{
                maxWidth: '100%',
                border: '1px solid var(--mantine-color-gray-3)',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <img
                src={answerData.backImage}
                alt="Body diagram - Back view"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
            </Box>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    );
  }

  // Single view (front or back)
  const imageSrc = hasFront ? answerData.frontImage : answerData.backImage;
  const viewLabel = hasFront ? 'Front' : 'Back';

  return (
    <Stack gap="xs">
      {notes.label && (
        <Text size="sm" fw={500}>
          {notes.label}
        </Text>
      )}
      <Box
        style={{
          maxWidth: '100%',
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <img
          src={imageSrc}
          alt={`Body diagram - ${viewLabel} view`}
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </Box>
    </Stack>
  );
}
